import { Loader2, Upload as UploadIcon, Cog, AlertTriangle, X } from 'lucide-react';
import { useUploadProgressContext } from '../contexts/UploadProgressContext';

// Coerce qualunque valore di errore in una stringa renderizzabile.
// Un detail FastAPI 422 è un array di {loc,msg,type}; un 500 strutturato
// può essere un oggetto. Senza questa coercizione React lancia "Objects are
// not valid as a React child" → schermata bianca su tutta l'app.
function errText(v) {
  if (v == null || v === '') return '';
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.map(errText).filter(Boolean).join(' · ');
  if (typeof v === 'object') return v.msg || v.detail || v.message || JSON.stringify(v);
  return String(v);
}

function UploadCard({ job, onClose }) {
  const { phase, percent = 0, processed, total, title, subtitle, errorMessage, errorDetail } = job;
  const isUploading = phase === 'uploading';
  const isError = phase === 'error';
  const hasPct = typeof percent === 'number' && !isNaN(percent) && !isError;

  let iconBg, Icon;
  if (isError) { iconBg = 'bg-rose-100 text-rose-600'; Icon = AlertTriangle; }
  else if (isUploading) { iconBg = 'bg-blue-100 text-blue-600'; Icon = UploadIcon; }
  else { iconBg = 'bg-emerald-100 text-emerald-600'; Icon = Cog; }

  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-4 md:p-5">
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-9 h-9 md:w-10 md:h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon className={`h-4 w-4 md:h-5 md:w-5 ${(!isUploading && !isError) ? 'animate-spin' : ''}`} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-gray-900 truncate text-sm">
            {title || (isError ? 'Errore' : isUploading ? 'Caricamento file' : 'Elaborazione')}
          </h3>
          {subtitle && <p className="text-xs text-gray-500 truncate">{subtitle}</p>}
        </div>
        <button aria-label={isError ? 'Chiudi' : 'Interrompi elaborazione'}
          onClick={onClose}
          className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-xl text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          title={isError ? 'Chiudi' : 'Interrompi elaborazione'}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {isError ? (
        <>
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-3 text-sm text-rose-900 break-words">
            {errText(errorMessage) || 'Errore non specificato'}
          </div>
          {errText(errorDetail) && (
            <details className="mt-2">
              <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">Dettagli tecnici</summary>
              <pre className="mt-2 text-[11px] font-mono bg-gray-50 border border-gray-200 rounded-lg p-2 overflow-auto max-h-40 whitespace-pre-wrap break-all">{errText(errorDetail)}</pre>
            </details>
          )}
          <button
            onClick={onClose}
            className="mt-3 w-full bg-[#48484A] text-white rounded-xl py-2 text-sm font-semibold hover:bg-[#3A3A3C] transition-colors"
          >
            Chiudi
          </button>
        </>
      ) : hasPct ? (
        <>
          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-200 ${isUploading ? 'bg-blue-500' : 'bg-emerald-500'}`}
              style={{ width: `${Math.max(0, Math.min(100, percent))}%` }}
            />
          </div>
          <div className="flex justify-between mt-1.5 text-xs text-gray-500">
            <span>
              {isUploading ? 'Caricamento' : 'Elaborazione'} in corso
              {typeof processed === 'number' && typeof total === 'number' && total > 0 ? ` — ${processed} di ${total}` : ''}
            </span>
            <span className="font-mono font-semibold text-gray-700">{Math.round(percent)}%</span>
          </div>
        </>
      ) : (
        <div className="flex items-center gap-3">
          <Loader2 className="h-4 w-4 animate-spin text-emerald-600 flex-shrink-0" />
          <p className="text-xs text-gray-600">Il server sta elaborando i dati…</p>
        </div>
      )}
    </div>
  );
}

export default function UploadProgressOverlay() {
  const { jobs, cancelJob, removeJob } = useUploadProgressContext();
  const visible = [...jobs.values()].filter(j => j.open);
  if (visible.length === 0) return null;

  return (
    <div className="fixed left-3 right-3 bottom-[calc(env(safe-area-inset-bottom)+0.75rem)] md:left-auto md:right-4 md:bottom-4 md:w-full md:max-w-sm z-[60] flex flex-col gap-2">
      {visible.map(job => (
        <UploadCard
          key={job.id}
          job={job}
          onClose={() => (job.phase === 'error' ? removeJob : cancelJob)(job.id)}
        />
      ))}
    </div>
  );
}
